// app/components/privacy-graph-scrolly/elements/modal.tsx
//
// Shared modal used by beats 4, 5, 6. SVG-rendered (lives inside the
// scrolly's main <svg>) so it shares coordinate space with leader lines
// and node cards.
//
// Visual: white surface, --border 1px stroke, shadow-md offset rect,
// colored eyebrow (mono uppercase) carrying the status word + title +
// body rows + optional savings row. Status is encoded entirely through
// the eyebrow color and the connected node's stroke — no edge stripe.

import type { ReactNode } from "react";
import { MODAL, type NodeState as _ } from "../graph-layout"; // noqa: keep type imports tidy
import type { BeatModalData, ModalRow } from "../keyframe-data";

interface ModalProps {
  data: BeatModalData;
  /** Top-left x in SVG coordinates. */
  x: number;
  /** Top-left y in SVG coordinates. */
  y: number;
  /** Modal width in SVG units. Defaults to 280. */
  w?: number;
  /** Optional fixed/minimum surface height for consistent modal slots. */
  minH?: number;
}

const ROW_H = 20;
const NESTED_INDENT = 14;
// Approximate width-per-char for 12px Geist sans. Used for word-wrap so
// long body strings break onto multiple lines instead of overflowing the
// modal box (SVG <text> does not auto-wrap).
const BODY_CHAR_W = 6.4;

function isRow(b: string | ModalRow): b is ModalRow {
  return typeof b !== "string";
}

function wrapText(text: string, maxWidth: number): string[] {
  const maxChars = Math.max(8, Math.floor(maxWidth / BODY_CHAR_W));
  const words = text.split(/\s+/);
  const lines: string[] = [];
  let current = "";
  for (const word of words) {
    const candidate = current ? `${current} ${word}` : word;
    if (candidate.length <= maxChars) {
      current = candidate;
    } else {
      if (current) lines.push(current);
      current = word;
    }
  }
  if (current) lines.push(current);
  return lines.length > 0 ? lines : [text];
}

/** Layout-time count of body lines that wrapped strings will occupy. */
function bodyLineCount(body: BeatModalData["body"], innerW: number): number {
  let count = 0;
  for (const b of body) {
    if (isRow(b)) count += 1;
    else count += wrapText(b, innerW).length;
  }
  return count;
}

function CheckGlyph({ x, y, color }: { x: number; y: number; color: string }) {
  return (
    <path
      d={`M${x},${y + 5} L${x + 3},${y + 8} L${x + 8},${y + 1}`}
      stroke={color}
      strokeWidth={1.5}
      fill="none"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  );
}

export function Modal({ data, x, y, w = 280, minH }: ModalProps) {
  const eyebrowColor = MODAL.eyebrow[data.status];

  const innerX = MODAL.paddingX;
  const innerW = w - innerX - MODAL.paddingX;

  // Compute height with wrap-aware line count so wrapped strings don't bleed
  // past the surface rect.
  const titleAreaH = 36;
  const totalBodyLines = bodyLineCount(data.body, innerW);
  const bodyH = totalBodyLines * ROW_H;
  const savingsH = data.savings ? 28 : 0;
  const contentH = MODAL.paddingY * 2 + titleAreaH + bodyH + savingsH;
  const h = Math.max(contentH, minH ?? 0);

  // First body element y. Title baseline ~ 30 from top.
  const eyebrowY = MODAL.paddingY + 10;
  const titleY = eyebrowY + 18;
  const bodyStartY = MODAL.paddingY + titleAreaH + 12;
  const savingsY = bodyStartY + bodyH + 8;

  return (
    <g transform={`translate(${x}, ${y})`}>
      {/* Shadow */}
      <rect x={0} y={2} width={w} height={h} rx={MODAL.radius} fill="rgba(17,22,29,0.08)" />
      {/* Surface */}
      <rect
        width={w}
        height={h}
        rx={MODAL.radius}
        fill={MODAL.surface}
        stroke={MODAL.border}
        strokeWidth={1}
      />
      {/* Eyebrow (mono uppercase) — carries the status signal via color */}
      <text
        x={innerX}
        y={eyebrowY}
        fontSize={13}
        letterSpacing="0.12em"
        fill={eyebrowColor}
        fontFamily="var(--font-mono, ui-monospace, SFMono-Regular, monospace)"
      >
        {data.eyebrow}
      </text>
      {/* Title */}
      <text
        x={innerX}
        y={titleY}
        fontSize={16}
        fontWeight={600}
        fill={MODAL.title}
        fontFamily="var(--font-sans, system-ui, sans-serif)"
      >
        {data.title}
      </text>

      {/* Body — string entries wrap to multiple lines; row entries stay one line each. */}
      {(() => {
        const elements: ReactNode[] = [];
        let lineCursor = 0;
        data.body.forEach((b, i) => {
          const rowY = bodyStartY + lineCursor * ROW_H;
          if (isRow(b)) {
            const indent = b.nested ? NESTED_INDENT : 0;
            elements.push(
              <g key={i}>
                {b.nested && (
                  <line
                    x1={innerX + 4}
                    y1={rowY - 14}
                    x2={innerX + 4}
                    y2={rowY - 4}
                    stroke={MODAL.border}
                    strokeWidth={1}
                  />
                )}
                {b.nested && (
                  <line
                    x1={innerX + 4}
                    y1={rowY - 4}
                    x2={innerX + 10}
                    y2={rowY - 4}
                    stroke={MODAL.border}
                    strokeWidth={1}
                  />
                )}
                <text
                  x={innerX + indent}
                  y={rowY}
                  fontSize={12}
                  fontWeight={b.nested ? 400 : 500}
                  fill={MODAL.body}
                  fontFamily="var(--font-sans, system-ui, sans-serif)"
                >
                  {b.label}
                </text>
                {b.ok && (
                  <CheckGlyph x={innerX + innerW - 86} y={rowY - 9} color={MODAL.accent.ok} />
                )}
                <text
                  x={innerX + innerW}
                  y={rowY}
                  fontSize={12}
                  fontWeight={500}
                  textAnchor="end"
                  fill={MODAL.title}
                  fontFamily="var(--font-sans, system-ui, sans-serif)"
                >
                  {b.value}
                </text>
              </g>,
            );
            lineCursor += 1;
          } else {
            const lines = wrapText(b, innerW);
            lines.forEach((line, li) => {
              elements.push(
                <text
                  key={`${i}-${li}`}
                  x={innerX}
                  y={bodyStartY + (lineCursor + li) * ROW_H}
                  fontSize={12}
                  fill={MODAL.body}
                  fontFamily="var(--font-sans, system-ui, sans-serif)"
                >
                  {line}
                </text>,
              );
            });
            lineCursor += lines.length;
          }
        });
        return elements;
      })()}

      {/* Savings row */}
      {data.savings && (
        <g>
          <line
            x1={innerX}
            y1={savingsY - 10}
            x2={innerX + innerW}
            y2={savingsY - 10}
            stroke={MODAL.border}
            strokeWidth={1}
          />
          <text
            x={innerX}
            y={savingsY + 4}
            fontSize={12}
            letterSpacing="0.1em"
            fill={MODAL.body}
            fontFamily="var(--font-mono, ui-monospace, SFMono-Regular, monospace)"
          >
            {data.savings.label}
          </text>
          <text
            x={innerX + innerW}
            y={savingsY + 4}
            fontSize={13}
            fontWeight={600}
            textAnchor="end"
            fill={MODAL.title}
            fontFamily="var(--font-sans, system-ui, sans-serif)"
          >
            {data.savings.value}
          </text>
        </g>
      )}
    </g>
  );
}

/** Compute the modal's height for layout-time anchoring. Mirrors layout in render. */
export function modalHeight(data: BeatModalData, w = 280): number {
  const innerX = MODAL.paddingX;
  const innerW = w - innerX - MODAL.paddingX;
  const titleAreaH = 36;
  const bodyH = bodyLineCount(data.body, innerW) * ROW_H;
  const savingsH = data.savings ? 28 : 0;
  return MODAL.paddingY * 2 + titleAreaH + bodyH + savingsH;
}
